import { wccModules } from 'enums/wccModules';
import { getTopicRibbon } from 'helpers/topics';
import { Func } from 'interfaces/func';
import { inject, injectable } from 'inversify';
import { PureComputed, Subscribable, SubscribableOrNullableValue } from 'knockout';
import { CurrentUserManager } from 'managers/currentUser';
import { IWCCStorageManager } from 'managers/iStorage';
import overlayManager from 'managers/overlay';
import { Person } from 'models/person';
import { JSONSimpleTopic, SimpleTopic } from 'models/simpleTopic';
import { Topic } from 'models/topic';
import { TopicRibbon } from 'models/topicRibbon';
import WCCError from 'models/wccError';
import './box.html';
import './box.scss';

const { discussionId } = settings;

components.preload('topic-summary', 'topic-image');

export interface TopicBoxVMConfig {
    topic?: SubscribableOrNullableValue<SimpleTopic | Topic>
    theme?: SubscribableOrNullableValue<TopicBoxVMTheme>
    compact?: SubscribableOrNullableValue<boolean>
    hideNotifications?: SubscribableOrNullableValue<boolean>
    showExpander?: SubscribableOrNullableValue<boolean>
    canActivate?: SubscribableOrNullableValue<boolean>

    activate?: Action<[topic: SimpleTopic]> | Func<Promise<void>, [topic: SimpleTopic]>
}

export type TopicBoxVMTheme = 'classic' | 'compact' | 'small' | 'small-rounded'

@injectable()
export default class TopicBoxVM {
    private currentUser: Subscribable<Person | undefined>

    labels = labels

    topic: Subscribable<SimpleTopic | undefined>
    topicId: Subscribable<string | undefined>

    imageURL: Subscribable<string | undefined>

    title: Subscribable<string | undefined>
    quickSell: Subscribable<string | undefined>
    toDoCount: Subscribable<number>

    attrTitleQuickSell: PureComputed<string>

    ribbon: Subscribable<TopicRibbon | undefined>

    canBeActivated: Subscribable<boolean>

    isToDoCountShown: Subscribable<boolean>
    isExpanderShown: Subscribable<boolean>

    constructor(
        @inject(wccModules.componentConfig) private config: TopicBoxVMConfig,
        @inject(wccModules.componentInfo) info: ko.components.ComponentInfo,
        @inject(wccModules.storage) storage: IWCCStorageManager
    ) {
        const topicOrSimpleTopic = ko.flattenComputed(config.topic);
        const theme = ko.pureComputed(() => this.getTheme(config));
        const shouldHideNotifications = ko.flattenComputed(config.hideNotifications, false);
        this.isExpanderShown = ko.flattenComputed(config.showExpander, false);

        const currentUserManager = storage.get(CurrentUserManager, { discussionId });
        this.currentUser = currentUserManager.pluck(m => m.person);

        this.topic = topicOrSimpleTopic.mapNotNull<JSONSimpleTopic>(t => t.toJson()).toEntity(
            jsonTopic => new SimpleTopic(jsonTopic),
            (topic, jsonTopic) => topic.update(jsonTopic));

        this.ribbon = ko.pureComputed(() => {
            const user = this.currentUser();
            const topic = this.topic();

            if (user != undefined && topic != undefined)
                return getTopicRibbon(user, topic);
        });

        this.topicId = this.topic.pluck(topic => topic.discussionTopicId);
        this.imageURL = this.topic.pluck(topic => topic.imageURL);
        this.title = this.topic.pluck(topic => topic.title);
        this.quickSell = this.topic.pluck(topic => topic.quickSell);

        this.attrTitleQuickSell = ko.pureComputed(() => this.title() + (this.quickSell() ? ` - ${this.quickSell()}` : ''));

        this.toDoCount = this.topic.pluck(topic => topic.toDoCount, 0);
        this.isToDoCountShown = this.toDoCount.is(count => count > 0);

        const canBeActivated = ko.flattenComputed(config.canActivate, true);
        this.canBeActivated = ko.pureComputed(() => config.activate != undefined && canBeActivated());

        ko.applyBindingsToNode(info.element, {
            'css': {
                'topic__box': true,
                'topic__box--theme-compact': theme.is('compact', 'small', 'small-rounded'),
                'topic__box--theme-small': theme.is('small', 'small-rounded'),
                'topic__box--theme-rounded': theme.is('small-rounded'),
                'topic__box--no-notifications': shouldHideNotifications,
            }
        });
    }

    expand = () => {
        overlayManager.toOverlay({ id: 'wcc-components-topic-box-expanded-topic' }, { data: this });
    }

    activate = (m: any, e: JQueryEventObject) => {
        e.preventDefault();
        e.stopPropagation();

        const topic = this.topic();

        if (topic == undefined)
            throw new WCCError(messages.InvalidRequest);

        system.confirm(messages.AreYouSureYouWantToActivateTopic.replace('{0}', topic.title()), () => this.config.activate?.(topic));
    }

    private getTheme(config: TopicBoxVMConfig): TopicBoxVMTheme {
        const isCompact = ko.unwrap(config.compact) ?? false;

        if (isCompact)
            return 'compact';

        return ko.unwrap(config.theme) ?? 'classic';
    }
}